/**
 * Arcℏimedian DSaaS — Error Boundary
 * Catches unhandled render errors and shows a recovery UI.
 * Reports to Sentry when VITE_SENTRY_DSN is set.
 */

import React from 'react';
import { theme as T } from '../theme';

interface Props {
  children: React.ReactNode;
  inline?: boolean; // show compact inline error instead of full-screen
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

function getLang(): 'pt' | 'en' {
  try {
    return (localStorage.getItem('archimedian_lang') ?? 'en') === 'pt' ? 'pt' : 'en';
  } catch {
    return 'en';
  }
}

const STRINGS = {
  en: {
    title: 'Something went wrong',
    body: 'An unexpected error occurred. Our team has been notified.',
    devDetails: 'Technical details (dev)',
    goHome: 'Go to home',
    reload: 'Try again',
  },
  pt: {
    title: 'Algo deu errado',
    body: 'Ocorreu um erro inesperado. Nossa equipe foi notificada.',
    devDetails: 'Detalhes técnicos (dev)',
    goHome: 'Voltar ao início',
    reload: 'Tentar novamente',
  },
};

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null, errorId: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary]', error, info);
    try {
      if ((window as any).Sentry) {
        const id = (window as any).Sentry?.captureException(error, {
          extra: { componentStack: info.componentStack },
        });
        this.setState({ errorId: id ?? null });
      }
    } catch { /* Sentry not available */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: null });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const lang = getLang();
    const s = STRINGS[lang];

    if (this.props.inline) {
      return (
        <div style={{
          padding: 40, textAlign: 'center', color: T.textDim,
          background: T.bgCard, borderRadius: T.radius, border: `1px solid ${T.border}`,
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>⚡</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, marginBottom: 8 }}>{s.title}</div>
          <div style={{ fontSize: 13, color: T.textDim, marginBottom: 16 }}>{s.body}</div>
          <button
            onClick={() => { this.setState({ hasError: false, error: null, errorId: null }); window.location.reload(); }}
            style={{
              padding: '8px 20px', background: T.accent, color: '#fff',
              border: 'none', borderRadius: T.radiusSm, fontWeight: 600,
              fontSize: 13, cursor: 'pointer',
            }}
          >
            {s.reload}
          </button>
        </div>
      );
    }

    return (
      <div style={{
        minHeight: '100vh', background: T.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24, fontFamily: T.fontSans,
      }}>
        <div style={{
          maxWidth: 480, textAlign: 'center',
          background: T.bgCard, border: `1px solid ${T.border}`,
          borderRadius: T.radius, padding: 40,
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚡</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 10 }}>
            {s.title}
          </h1>
          <p style={{ color: T.textDim, fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            {s.body}
            {this.state.errorId && (
              <span style={{ display: 'block', marginTop: 8, fontSize: 12, color: T.textMuted, fontFamily: 'monospace' }}>
                ID: {this.state.errorId}
              </span>
            )}
          </p>

          {import.meta.env.DEV && this.state.error && (
            <details style={{
              marginBottom: 24, textAlign: 'left',
              background: T.surface, borderRadius: T.radiusSm, padding: 12,
            }}>
              <summary style={{ cursor: 'pointer', fontSize: 12, color: T.textMuted, marginBottom: 8 }}>
                {s.devDetails}
              </summary>
              <pre style={{ fontSize: 11, color: T.danger, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap' }}>
                {this.state.error.message}
                {'\n'}
                {this.state.error.stack?.split('\n').slice(0, 8).join('\n')}
              </pre>
            </details>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <button
              onClick={this.handleReset}
              style={{
                padding: '10px 24px', background: T.accent, color: '#fff',
                border: 'none', borderRadius: T.radiusSm,
                fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}
            >
              {s.goHome}
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 24px', background: 'none',
                border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
                color: T.textMuted, fontSize: 14, cursor: 'pointer',
              }}
            >
              {s.reload}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
